/**
 * The rocks in an asteroid field.
 *
 * A field has always been an orbit, a radius and a richness — a *region*, drawn
 * as a few faint rings and nothing else. There was nothing in it to fly between,
 * nothing to point at, and nothing to mine. This is what fills it.
 *
 * ── Why they are generated rather than stored ───────────────────────────────
 *
 * Same reason as every other body: the galaxy is a seed, not a database. Rolling
 * the rocks from `seed + field id` means every client draws the same field
 * without anyone sending it, a player who logs in tomorrow finds the same rocks
 * they left, and the server can check a mining claim against the same list
 * without keeping one.
 *
 * The index into that list is the rock's name. When mining lands, "rock 47 of
 * this field is worked out" is a fact both ends can agree on from the seed alone
 * — only the *depletion* has to be stored, which is a far smaller thing than
 * the field itself.
 *
 * ── Local coordinates, and why the field spins as one ───────────────────────
 *
 * Positions are relative to the field's own frame, not the system's. The caller
 * places that frame on the orbit and turns it; the rocks do not each carry an
 * orbit of their own.
 *
 * That is a deliberate simplification. Strictly, an inner rock should orbit
 * faster than an outer one and the belt should shear over time. Doing it
 * properly means a Kepler solve per rock per frame — hundreds of them — to
 * animate something nobody can perceive, because you cannot see both edges of a
 * belt at once at any distance where the rocks are visible at all.
 */
import { Rng } from './rng.js';
/**
 * Rocks per generator unit of the band's circumference.
 *
 * ── Why density rather than a count ─────────────────────────────────────────
 *
 * A count has to be picked per kind, and a belt round a star then needs a
 * different number from a cluster round a planet — for the same reason a road
 * needs more paint than a driveway. They are not different kinds of thing, only
 * different lengths of one. Density states what is actually true of both: how
 * thick the rocks are on the ground.
 *
 * So the belt/cluster split is gone from here. A bigger ring gets more rocks
 * because it is bigger, which is what the eye expects and what two hand-tuned
 * ranges were approximating.
 */
const PER_UNIT = 0.5;
/**
 * The most any one field will hold.
 *
 * A draw-cost bound rather than a design one: a field is a single instanced
 * mesh, and a belt a few thousand units across would otherwise ask for tens of
 * thousands of rocks. Past this the ring is dense enough that the spacing
 * widening is not something you can see.
 */
const MAX_ROCKS = 900;
/**
 * How big a rock is, **in generator units**, before richness leans on it.
 *
 * Generator units, not world ones — the whole system group is blown up by
 * `GAME_SCALE` as a single step, so anything sized in world units here comes out
 * twenty times too big. That is exactly what happened: rocks specified at 3–14
 * were drawn at 60–280 across, against a starter ship about 1.9 units long.
 *
 * The ceiling is roughly a starter ship. These are things you fly *between* and
 * eventually pick apart, not a wall of boulders — which is also why a field has
 * no mass lock at all.
 */
const SIZE = { min: 0.15, max: 3 };
/**
 * The largest a rock can be, whatever richness says.
 *
 * Exported so the tests check that the cap is *applied* rather than that it
 * holds a particular value — tuning the number above should not fail a test.
 */
export const MAX_ROCK_RADIUS = SIZE.max;
/** Hex from hue (0..1), saturation and lightness. */
function hsl(hue, saturation, lightness) {
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const sector = ((((hue % 1) + 1) % 1) * 6);
    const second = chroma * (1 - Math.abs((sector % 2) - 1));
    const base = lightness - chroma / 2;
    const [r, g, b] = sector < 1 ? [chroma, second, 0]
        : sector < 2 ? [second, chroma, 0]
            : sector < 3 ? [0, chroma, second]
                : sector < 4 ? [0, second, chroma]
                    : sector < 5 ? [second, 0, chroma]
                        : [chroma, 0, second];
    const byte = (value) => Math.round((value + base) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${byte(r)}${byte(g)}${byte(b)}`;
}
/**
 * The colour a field is drawn in.
 *
 * One seeded pastel, and that is the whole palette — the rocks vary around it in
 * the shader, from each instance's own position, which costs nothing and gives
 * every rock its own shade rather than one of a handful.
 *
 * An earlier version handed out six fixed shades per field and the renderer drew
 * a mesh per shade. That capped the variation at six *and* cost six draw calls,
 * which is the wrong trade in both directions.
 *
 * Pastel by construction rather than by picking from a list: any hue, held to a
 * saturation and lightness that read as stone. These are lit only by their
 * edges, so a dark colour reads as no colour and a vivid one as a warning light.
 */
export function fieldTint(seed, fieldId) {
    const rng = Rng.forEntity(seed, 'asteroid-tint', fieldId);
    return hsl(rng.float(), 0.32 + rng.float() * 0.2, 0.6 + rng.float() * 0.12);
}
/** How much punishment a rock takes before it comes apart. */
export const ASTEROID_HEALTH = { min: 40, max: 400 };
/**
 * Every rock in a field, in the field's own frame.
 *
 * Deterministic: the same seed and field give the same rocks, in the same order,
 * on every machine and every session.
 *
 * The caller is expected to hold on to the result rather than calling this per
 * frame — it allocates a few hundred objects and is meant to run once, when a
 * system scene is built.
 */
export function asteroidsIn(seed, field) {
    const rng = Rng.forEntity(seed, 'asteroids', field.id);
    const tint = fieldTint(seed, field.id);
    // The ring's own length. A cluster round a planet and a belt round a star are
    // the same sum at different scales, which is the whole point of doing it this
    // way rather than with a number per kind.
    const circumference = Math.PI * 2 * field.orbit.semiMajorAxis;
    const count = Math.max(12, Math.min(MAX_ROCKS, Math.round(circumference * PER_UNIT)));
    const rocks = [];
    for (let index = 0; index < count; index++) {
        /*
         * All the way round, whatever it orbits.
         *
         * Both kinds ring their parent — a belt around a star, a cluster around a
         * planet — and the difference between them is how wide the band is, not
         * whether it closes. An earlier version gave clusters a short arc, which
         * made them a clump hanging beside a planet rather than something in orbit
         * around it.
         */
        /*
             * Evenly spaced, then nudged off it.
             *
             * Purely random angles clump: on any ring you get gaps you could fly a
             * station through beside knots three deep, because that is what random
             * looks like. Giving each rock a slot and jittering it inside that slot
             * gives an even field with no visible pattern — which is what a belt looks
             * like, and what "random" is usually being asked for.
             *
             * Most of a slot wide, so neighbours can nearly meet and the spacing never
             * reads as regular — but not a whole one, or they swap places and reopen
             * the gaps this exists to close.
             */
        const slot = (Math.PI * 2) / count;
        const angle = (index + 0.5) * slot + (rng.float() - 0.5) * slot * 0.85;
        /*
         * Off the centre line, biased towards it.
         *
         * Two rolls averaged rather than one: a flat spread puts as many rocks at
         * the very edge as in the middle, which reads as a wall with a hole in it.
         * This thins out towards the edges the way a real belt does, without
         * needing a distribution anybody has to explain.
         */
        /*
         * A cluster rides in a tighter band than a belt does.
         *
         * Both are rings; a belt spans its whole stated radius, while a cluster
         * gathers into a narrower one around its planet. Squeezing the band rather
         * than shortening the arc is what keeps it in orbit and still reads as the
         * denser thing.
         */
        const band = 1; //fit belt
        const spread = ((rng.float() + rng.float()) / 2 - 0.5) * field.radius * band;
        /*
         * Belts are flat.
         *
         * A small tilt off the band's own plane, in radians — a fraction of a degree
         * at these radii. Any more and a belt becomes a shell you cannot fly over.
         */
        /*
             * Above or below the band, in the belt's own frame.
             *
             * A distance along the ring's normal, not a tilt of the orbit. A tilted
             * orbit crosses the plane twice, so a belt given thickness that way is thin
             * at two points and thickest ninety degrees from them — it pinches. An
             * offset is thickness everywhere, which is what a belt has.
             *
             * A fraction of the band's width, biased to the middle by the same two-roll
             * average the radial spread uses: belts are flatter than they are wide, and
             * this keeps one flyable-over rather than a shell.
             */
        const height = ((rng.float() + rng.float()) / 2 - 0.5) * field.radius * 0.45;
        // Richness makes the rocks bigger as well as more valuable, so a field worth
        // crossing a system for looks like one before you scan it.
        const scale = SIZE.min + rng.float() * (SIZE.max - SIZE.min);
        rocks.push({
            index,
            body: 'asteroid',
            /*
             * Bigger rocks take more breaking, and richer fields hold tougher ones.
             *
             * Rounded, because health is an integer — see the field's doc comment.
             */
            health: Math.round(ASTEROID_HEALTH.min +
                (ASTEROID_HEALTH.max - ASTEROID_HEALTH.min) *
                    Math.min(1, (scale / SIZE.max) * (0.6 + field.richness * 0.3))),
            /*
             * The field's colour. The shader varies each rock around it.
             *
             * Kept on the rock rather than only on the field so anything that is not
             * the renderer — a scan readout, a minimap — has an answer without
             * knowing how the shader works.
             */
            color: tint,
            orbit: {
                ...field.orbit,
                semiMajorAxis: field.orbit.semiMajorAxis + spread,
                /*
                 * Where on the ring it sits.
                 *
                 * Its own anomaly rather than the field's, so the rocks are spread
                 * around the band instead of stacked at the one point the field happens
                 * to be at.
                 */
                trueAnomaly: angle,
            },
            height,
            /*
             * Richer fields have bigger rocks, up to a point.
             *
             * Capped at `SIZE.max`, because richness runs past two in places and an
             * uncapped multiplier put rocks half again the size of a starter ship —
             * which is not a thing you fly between.
             */
            radius: Math.min(SIZE.max, scale * (0.7 + field.richness * 0.5)),
            spin: {
                x: rng.float() * Math.PI * 2,
                y: rng.float() * Math.PI * 2,
                z: rng.float() * Math.PI * 2,
            },
            tumble: (rng.float() - 0.5) * 0.8,
        });
    }
    return rocks;
}
/**
 * How fast the whole field turns, in radians per second.
 *
 * The mean motion of its orbit — one revolution per period — applied to the
 * field as a unit. See the header for why the rocks do not each get their own.
 */
export function fieldSpin(field) {
    // Kepler's third law with the constant folded out: what matters is that outer
    // belts crawl and inner ones do not, at the same rate the planets do.
    const period = Math.pow(field.orbit.semiMajorAxis, 1.5);
    return period > 0 ? (Math.PI * 2) / (period * 60) : 0;
}
//# sourceMappingURL=asteroids.js.map